import { useMemo } from 'react';
import { SearchSuggest } from '../ui';
import {
    buildUnitOfMeasureOptions,
    displayUnitOfMeasure,
    resolveUnitOfMeasureInput,
} from '../../utils/unitOfMeasureUtils';
import { useUserContext } from '../../contexts/UserContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';

interface UnitOfMeasureSearchSuggestProps {
    value: string;
    onChange: (value: string) => void;
    extraValues?: string[];
    inputId?: string;
    ariaLabel?: string;
    placeholder?: string;
    className?: string;
    /** When false, only the company catalog (plus `extraValues`) is offered. */
    includeEquipmentUnits?: boolean;
}

export const UnitOfMeasureSearchSuggest = ({
    value,
    onChange,
    extraValues = [],
    inputId,
    ariaLabel,
    placeholder = 'Search units of measure...',
    className,
    includeEquipmentUnits = true,
}: UnitOfMeasureSearchSuggestProps) => {
    const { company, companyOwner } = useUserContext();
    const { companyUnits } = useEquipmentContext();
    const companyId = (company?.id as string) || companyOwner?.companyId || '';

    const unitExtraValues = useMemo(() => {
        const fromUnits = includeEquipmentUnits
            ? companyUnits
                  .map((unit) => unit.unitOfMeasure)
                  .filter((raw): raw is string => Boolean(raw?.trim()))
            : [];
        return [...fromUnits, ...extraValues];
    }, [companyUnits, extraValues, includeEquipmentUnits]);

    const { catalogOptions, createUnit } = useCompanyUnitsOfMeasure(companyId, unitExtraValues);
    const options = useMemo(
        () => buildUnitOfMeasureOptions(unitExtraValues, catalogOptions),
        [unitExtraValues, catalogOptions],
    );
    const selectedValue = useMemo(() => {
        const trimmed = (value || '').trim();
        if (!trimmed) return '';
        const match = options.find(
            (option) =>
                option.value === trimmed ||
                option.value.toLowerCase() === trimmed.toLowerCase() ||
                option.label.toLowerCase() === trimmed.toLowerCase(),
        );
        return match?.value ?? trimmed;
    }, [options, value]);

    const resolveValue = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return '';
        const exact = options.find(
            (option) =>
                option.value === trimmed ||
                option.value.toLowerCase() === trimmed.toLowerCase() ||
                option.label.toLowerCase() === trimmed.toLowerCase(),
        );
        if (exact) return exact.value;
        return resolveUnitOfMeasureInput(text);
    };

    return (
        <SearchSuggest
            options={options}
            value={selectedValue}
            onChange={onChange}
            allowNew
            newLabel="+ Add custom UM"
            placeholder={placeholder}
            inputId={inputId}
            ariaLabel={ariaLabel}
            className={className}
            resolveValue={resolveValue}
            formatDisplayValue={(stored) => {
                const option = options.find((item) => item.value === stored);
                if (option) return option.label;
                return displayUnitOfMeasure(stored, catalogOptions);
            }}
            onCommitNew={(text) => {
                if (!companyId || !text.trim()) return;
                void createUnit(text)
                    .then((created) => {
                        if (created.value !== value) onChange(created.value);
                    })
                    .catch((error) => {
                        console.error('Failed to add custom unit of measure:', error);
                    });
            }}
        />
    );
};
